import React, { useState, useEffect } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import AppBar from '@material-ui/core/AppBar';
import CircularProgress from '@material-ui/core/CircularProgress';
import ToggleFullscreenButton from './ToggleFullScreenButton/ToggleFullScreenButton';
import Toolbar from '@material-ui/core/Toolbar';
import Menu from './Menu/Menu';

import { useAppState } from '../../state';
import { countdownOverride, effectiveSecondsRemaining, setCallSecondsRemaining } from '../../utils/callTimeLimit';
import { participantLabel } from '../../utils/participantLabels';
import { useParams } from 'react-router-dom';
import useRoomState from '../../hooks/useRoomState/useRoomState';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import { Typography } from '@material-ui/core';
import FlipCameraButton from './FlipCameraButton/FlipCameraButton';
import LocalAudioLevelIndicator from './DeviceSelector/LocalAudioLevelIndicator/LocalAudioLevelIndicator';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      backgroundColor: theme.palette.background.default,
    },
    toolbar: {
      [theme.breakpoints.down('xs')]: {
        padding: 0,
      },
    },
    rightButtonContainer: {
      display: 'flex',
      alignItems: 'center',
      marginLeft: 'auto',
    },
    form: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      [theme.breakpoints.up('md')]: {
        marginLeft: '2.2em',
      },
    },
    textField: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      maxWidth: 200,
    },
    loadingSpinner: {
      marginLeft: '1em',
    },
    displayName: {
      margin: '1.1em 0.6em',
      minWidth: '200px',
      fontWeight: 600,
    },
    joinButton: {
      margin: '1em',
    },
  }),
);

let roomData: any = {};
let roomOver = false;
let tokenResolved = false;

interface MenuBarProps {
  // The call was connected and has ended: show that instead of "Connected as ...".
  callEnded?: boolean;
}

export default function MenuBar({ callEnded = false }: MenuBarProps) {
  const classes = useStyles();
  const { URLRoomName } = useParams<{ URLRoomName?: string }>();
  const { user, getToken, isFetching } = useAppState();
  const { isConnecting, connect, isAcquiringLocalTracks } = useVideoContext();
  const roomState = useRoomState();

  const [name, setName] = useState<string>(user?.displayName || '');
  const [roomName, setRoomName] = useState<string>('');
  const [recordParticipantConnect, setRecordParticipantConnect] = useState<boolean>(false);

  useEffect(() => {
    // ?countdown=NNN (dev only) pretends NNN seconds are left, to try the countdown without an hour-long call.
    // Read before anything else: it is kept for later pages, and the page may have no join token.
    let countdownOverrideSeconds: number | null = null;
    try {
      countdownOverrideSeconds = countdownOverride(window.location.search, window.location.hostname, window.localStorage);
    } catch (e) {
      countdownOverrideSeconds = countdownOverride(window.location.search, window.location.hostname, null);
    }
    const resolveRoomToken = (token: string) => {
      let apierr: string = '';
      getToken(token, '').then(data => {
        apierr = data;
        roomData = JSON.parse(data);
        console.log(roomData);
        const secondsRemaining = effectiveSecondsRemaining(
          countdownOverrideSeconds,
          roomData.maxDurationSeconds,
          roomData.secondsRemaining,
        );
        console.log('call time limit: server secondsRemaining=%s override=%s using=%s', roomData.secondsRemaining, countdownOverrideSeconds, secondsRemaining);
        setCallSecondsRemaining(secondsRemaining);
        setRoomName(roomData.roomName);
        setName(roomData.participant);
        setRecordParticipantConnect(roomData.recordParticipantConnect);
        //connect(roomData.tokens);
      }).catch((err: any) => {
        if (apierr === 'Room Exists') {
          console.log(apierr);
          return;
        }
        roomOver = true;
        console.log(err);
        console.log(apierr);
        alert(apierr);
      });
    };

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const token = urlParams.get('token');
    if (token) {
      if (!tokenResolved) {
        tokenResolved = true;
        resolveRoomToken(token);
      }
    } else {
      alert('Invalid Token Argument');
    }
  }, [URLRoomName, getToken]);

  const isRoomReady = () => {
    if (isConnecting || isFetching || isAcquiringLocalTracks || !name || !roomName) {
      return false;
    }
    connect(roomData.tokens);
    return (roomOver = true);
  };

  return (
    <AppBar className={classes.container} position='static'>
      <Toolbar className={classes.toolbar}>
        {callEnded ? (
          <form className={classes.form}>
            <Typography className={classes.displayName} variant='body1'>
              Call ended.
            </Typography>
          </form>
        ) : roomState === 'disconnected' && !roomOver ? (
          <form className={classes.form}>
            <Typography className={classes.displayName} variant='body1'>
              Connecting as {participantLabel(name) || '...'}. {recordParticipantConnect ? 'This call is being recorded.' : ''}
            </Typography>
            {!isRoomReady() && <CircularProgress className={classes.loadingSpinner} />}
          </form>
        ) : (
          <form className={classes.form}>
            <Typography className={classes.displayName} variant='body1'>
              Connected as {participantLabel(name) || ''}. {recordParticipantConnect ? 'This call is being recorded.' : ''}
            </Typography>
          </form>
        )}

        <div className={classes.rightButtonContainer}>
          <FlipCameraButton />
          <LocalAudioLevelIndicator />
          <ToggleFullscreenButton />
          <Menu />
        </div>
      </Toolbar>
    </AppBar>
  );
}
